import React, { useState, useEffect, useCallback } from 'react';
import AdminNav from '../components/AdminNav';
import {
  getVolunteers,
  getAssignments,
  getShiftTimes,
  updateConfirmationStatus,
  updateVolunteerConfirmationMeta,
  updateVolunteerEmailStatus,
  getMessageTemplates,
  DEFAULT_MESSAGE_TEMPLATES,
  saveConfirmationToken,
} from '../services/firebase';
import './Admin.css';

const DAY_ORDER = ['Thu-April-9', 'Fri-April-10', 'Sat-April-11', 'Sun-April-12'];

const DAY_LABELS_EN = {
  'Thu-April-9': 'Thu, April 9', 'Fri-April-10': 'Fri, April 10',
  'Sat-April-11': 'Sat, April 11', 'Sun-April-12': 'Sun, April 12',
};
const DAY_LABELS_ES = {
  'Thu-April-9': 'Jue, 9 de Abril', 'Fri-April-10': 'Vie, 10 de Abril',
  'Sat-April-11': 'Sáb, 11 de Abril', 'Sun-April-12': 'Dom, 12 de Abril',
};

function isSpanish(congregation) { return /spanish/i.test(congregation || ''); }

function renderConfirmationEmail(template, volunteerName, shifts, confirmLink) {
  const dayLabels = template.lang === 'es' ? DAY_LABELS_ES : DAY_LABELS_EN;
  const hDay = template.tableHeaderDay || 'Day';
  const hLoc = template.tableHeaderLocation || 'Location';
  const hTime = template.tableHeaderTime || 'Time';
  const rows = shifts.map(s => `
    <tr>
      <td style="padding:9px 14px;border-bottom:1px solid #e2e8f0;">${dayLabels[s.day] || s.day}</td>
      <td style="padding:9px 14px;border-bottom:1px solid #e2e8f0;">${s.location}</td>
      <td style="padding:9px 14px;border-bottom:1px solid #e2e8f0;font-weight:600;">${s.shiftTime}</td>
    </tr>`).join('');
  const table = `<table style="border-collapse:collapse;width:100%;margin:20px 0;border:1px solid #e2e8f0;border-radius:6px;overflow:hidden;">
    <thead><tr style="background:#2563eb;color:white;">
      <th style="padding:10px 14px;text-align:left;font-weight:600;">${hDay}</th>
      <th style="padding:10px 14px;text-align:left;font-weight:600;">${hLoc}</th>
      <th style="padding:10px 14px;text-align:left;font-weight:600;">${hTime}</th>
    </tr></thead>
    <tbody style="background:white;">${rows}</tbody>
  </table>`;
  const pre = (template.emailHtmlPre || '').replace(/\{\{volunteerName\}\}/g, volunteerName);
  const isEs = template.lang === 'es';
  const confirmBlock = confirmLink ? `
    <div style="text-align:center;margin:24px 0;">
      <a href="${confirmLink}" style="display:inline-block;background:#16a34a;color:white;text-decoration:none;padding:14px 32px;border-radius:6px;font-size:16px;font-weight:700;">
        ${isEs ? '✅ Confirmar Asistencia' : '✅ Confirm My Attendance'}
      </a>
      <p style="font-size:12px;color:#94a3b8;margin-top:8px;">
        ${isEs ? 'O copie este enlace en su navegador:' : 'Or copy this link into your browser:'}<br>${confirmLink}
      </p>
    </div>` : '';
  return {
    subject: template.emailSubject,
    html: `<!DOCTYPE html><html lang="${template.lang || 'en'}"><body style="font-family:Arial,sans-serif;max-width:600px;margin:0 auto;padding:24px;color:#1e293b;">
  <div style="background:#1e293b;color:white;padding:20px 28px;border-radius:8px 8px 0 0;margin-bottom:0;">
    <h2 style="margin:0;font-size:20px;">SMPW Masters Event 2026</h2>
    <p style="margin:4px 0 0;opacity:0.75;font-size:14px;">Augusta, Georgia — April 9–12, 2026</p>
  </div>
  <div style="border:1px solid #e2e8f0;border-top:none;padding:28px;border-radius:0 0 8px 8px;">
    ${pre}${table}${confirmBlock}${template.emailHtmlPost || ''}
    <p style="margin-top:28px;padding-top:16px;border-top:1px solid #e2e8f0;color:#64748b;font-size:12px;">SMPW Scheduling Team – Augusta Masters</p>
  </div>
</body></html>`,
  };
}

function ConfirmationTracking({ onLogout }) {
  const [volunteers, setVolunteers] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [shiftTimes, setShiftTimes] = useState([]);
  const [templateMap, setTemplateMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [pendingOpen, setPendingOpen] = useState(false);
  const [confirmedOpen, setConfirmedOpen] = useState(false);
  const [pendingSubFilter, setPendingSubFilter] = useState('all'); // 'all' | 'emailed' | 'not-contacted'
  const [confirmSearch, setConfirmSearch] = useState('');
  const [bulkSending, setBulkSending] = useState(null); // null | 'initial' | 'reminder'
  const [bulkProgress, setBulkProgress] = useState(null); // null | { done, total }

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    const [volData, assignmentData, shiftTimeData, tmplDocs] = await Promise.all([
      getVolunteers(),
      getAssignments(),
      getShiftTimes(),
      getMessageTemplates(),
    ]);
    setVolunteers(volData);
    setAssignments(assignmentData);
    setShiftTimes(shiftTimeData);
    const map = {};
    for (const id of ['confirmation_en', 'confirmation_es']) {
      const fromDB = tmplDocs.find(d => d.id === id);
      map[id] = { ...DEFAULT_MESSAGE_TEMPLATES[id], ...(fromDB || {}) };
    }
    setTemplateMap(map);
    setLoading(false);
  };

  const getVolunteerById = (id) => volunteers.find(v => v.id === id);

  // Build one group per assigned volunteer across all assignments
  const getVolunteerGroups = () => {
    const groups = {};

    (assignments || []).forEach(assignment => {
      const { day, location, shiftTime } = assignment;
      const shiftId = `${day}_${location}_${shiftTime}`.replace(/\s+/g, '_');

      (assignment.volunteers || []).filter(Boolean).forEach((volunteerId, slotIndex) => {
        const volunteer = getVolunteerById(volunteerId);
        if (!volunteer) return;

        if (!groups[volunteerId]) {
          groups[volunteerId] = {
            volunteerId,
            volunteer,
            shifts: [],
            notes: volunteer.confirmationNotes || '',
            followUp: volunteer.confirmationFollowUp || false,
          };
        }

        const status = volunteer[`confirmation_${shiftId}`] || 'pending';
        groups[volunteerId].shifts.push({ day, location, shiftTime, shiftId, status, slotIndex });
      });
    });

    return Object.values(groups);
  };

  const handleStatusChange = async (volunteerId, shiftId, status) => {
    setVolunteers(prev =>
      prev.map(v => v.id === volunteerId ? { ...v, [`confirmation_${shiftId}`]: status } : v)
    );
    await updateConfirmationStatus(volunteerId, shiftId, status);
  };

  const handleMetaChange = async (volunteerId, meta) => {
    setVolunteers(prev =>
      prev.map(v => v.id === volunteerId ? { ...v, ...meta } : v)
    );
    await updateVolunteerConfirmationMeta(volunteerId, meta);
  };

  const handleBulkSend = async (groups, isReminder) => {
    const eligible = groups.filter(g => g.volunteer.email);
    if (eligible.length === 0) return alert('No volunteers in this group have an email address on file.');
    if (!window.confirm(`Send ${isReminder ? 'reminder ' : ''}confirmation emails to ${eligible.length} volunteer${eligible.length !== 1 ? 's' : ''}?`)) return;

    setBulkSending(isReminder ? 'reminder' : 'initial');
    setBulkProgress({ done: 0, total: eligible.length });

    for (const group of eligible) {
      const vol = group.volunteer;
      try {
        const lang = isSpanish(vol.congregation) ? 'es' : 'en';
        const template = templateMap[`confirmation_${lang}`] || templateMap['confirmation_en'];
        const volunteerName = `${vol.firstName} ${vol.lastName}`;
        const confirmedShifts = group.shifts.filter(s => s.status === 'confirmed');
        const allShifts = confirmedShifts.length > 0 ? confirmedShifts : group.shifts;
        const shiftsPayload = allShifts.map(s => ({ day: s.day, location: s.location, shiftTime: s.shiftTime }));

        const confirmToken = crypto.randomUUID();
        const shiftIds = allShifts.map(s => s.shiftId);
        const shiftDetails = allShifts.map(s => ({ day: s.day, location: s.location, shiftTime: s.shiftTime, shiftId: s.shiftId }));
        await saveConfirmationToken(group.volunteerId, confirmToken, shiftIds, shiftDetails);
        const confirmLink = `https://www.augustapwi.com/confirm?t=${confirmToken}`;

        const body = { to: vol.email, volunteerName, shifts: shiftsPayload };
        if (template?.emailHtmlPre) {
          const rendered = renderConfirmationEmail(template, volunteerName, shiftsPayload, confirmLink);
          body.html = rendered.html;
          body.subject = isReminder ? `Reminder: ${rendered.subject}` : rendered.subject;
        }

        const res = await fetch('/api/send-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        const data = await res.json();
        if (data.success) {
          const sentAt = new Date().toISOString();
          await updateVolunteerEmailStatus(group.volunteerId, {
            messageId: data.messageId || null,
            status: 'pending',
            reason: null,
            sentAt,
          });
          setVolunteers(prev => prev.map(v => v.id === group.volunteerId
            ? { ...v, lastConfirmEmailMsgId: data.messageId || null, lastConfirmEmailStatus: 'pending', lastConfirmEmailSentAt: sentAt }
            : v
          ));
        }
      } catch { /* skip failed, continue with rest */ }
      setBulkProgress(p => ({ ...p, done: p.done + 1 }));
    }

    setBulkSending(null);
    setBulkProgress(null);
  };

  const getStats = (groups) => {
    const allShifts = groups.flatMap(g => g.shifts);
    return {
      volunteers: groups.length,
      total: allShifts.length,
      pending: allShifts.filter(s => s.status === 'pending').length,
      confirmed: allShifts.filter(s => s.status === 'confirmed').length,
      leftVM: allShifts.filter(s => s.status === 'left-vm').length,
      cannotMake: allShifts.filter(s => s.status === 'cannot-make').length,
      noResponse: allShifts.filter(s => s.status === 'no-response').length,
      followUp: groups.filter(g => g.followUp).length,
    };
  };

  if (loading) {
    return <div className="admin-container"><div className="loading">Loading...</div></div>;
  }

  const allGroups = getVolunteerGroups();
  const stats = getStats(allGroups);

  const filteredGroups = allGroups.filter(g => {
    if (filter === 'all') return true;
    if (filter === 'follow-up') return g.followUp;
    return g.shifts.some(s => s.status === filter);
  }).sort((a, b) => {
    if (a.followUp && !b.followUp) return -1;
    if (!a.followUp && b.followUp) return 1;
    const lastCompare = (a.volunteer.lastName || '').localeCompare(b.volunteer.lastName || '');
    if (lastCompare !== 0) return lastCompare;
    return (a.volunteer.firstName || '').localeCompare(b.volunteer.firstName || '');
  });

  const FILTERS = [
    { value: 'all',          label: 'All',          count: stats.volunteers },
    { value: 'pending',      label: 'Pending',      count: stats.pending },
    { value: 'confirmed',    label: 'Confirmed',    count: stats.confirmed },
    { value: 'left-vm',      label: 'Left VM',      count: stats.leftVM },
    { value: 'cannot-make',  label: 'Cannot Make',  count: stats.cannotMake },
    { value: 'no-response',  label: 'No Response',  count: stats.noResponse },
    { value: 'follow-up',    label: 'Follow-Up',    count: stats.followUp },
  ];

  return (
    <div className="admin-container">
      <AdminNav onLogout={onLogout} />
      <header className="admin-header">
        <h1>Confirmation Tracking</h1>
        <p className="page-subtitle">Track assignment confirmations and send email confirmations to volunteers.</p>
      </header>

      <div className="confirmation-content">
        {/* Stats row */}
        <div className="confirmation-stats">
          <div className="stat-card">
            <div className="stat-label">Volunteers</div>
            <div className="stat-value">{stats.volunteers}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Total Shifts</div>
            <div className="stat-value">{stats.total}</div>
          </div>
          <div className="stat-card warning">
            <div className="stat-label">Pending</div>
            <div className="stat-value">{stats.pending}</div>
          </div>
          <div className="stat-card success">
            <div className="stat-label">Confirmed</div>
            <div className="stat-value">{stats.confirmed}</div>
          </div>
          <div className="stat-card info">
            <div className="stat-label">Left VM</div>
            <div className="stat-value">{stats.leftVM}</div>
          </div>
          <div className="stat-card danger">
            <div className="stat-label">Cannot Make</div>
            <div className="stat-value">{stats.cannotMake}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">No Response</div>
            <div className="stat-value">{stats.noResponse}</div>
          </div>
          <div className="stat-card followup-card">
            <div className="stat-label">Follow-Up</div>
            <div className="stat-value">{stats.followUp}</div>
          </div>
        </div>

        {/* Filter pills */}
        <div className="filter-pill-row">
          {FILTERS.map(f => (
            <button
              key={f.value}
              className={`filter-pill filter-pill-${f.value} ${filter === f.value ? 'active' : ''}`}
              onClick={() => setFilter(f.value)}
            >
              {f.label} <span className="pill-count">{f.count}</span>
            </button>
          ))}
        </div>

        {/* Search bar */}
        <div style={{ margin: '12px 0' }}>
          <input
            type="text"
            placeholder="Search by name, congregation, or email…"
            value={confirmSearch}
            onChange={e => setConfirmSearch(e.target.value)}
            style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }}
          />
        </div>

        {/* Volunteer cards — split into Pending and Confirmed accordion sections */}
        {(() => {
          const searchedGroups = confirmSearch.trim()
            ? filteredGroups.filter(g => {
                const q = confirmSearch.toLowerCase();
                const v = g.volunteer;
                return `${v.firstName} ${v.lastName}`.toLowerCase().includes(q) ||
                  (v.congregation || '').toLowerCase().includes(q) ||
                  (v.email || '').toLowerCase().includes(q);
              })
            : filteredGroups;

          if (searchedGroups.length === 0) {
            return <div className="no-data">No volunteers match this filter.</div>;
          }
          const pendingGroups   = searchedGroups.filter(g => !g.shifts.every(s => s.status === 'confirmed'));
          const confirmedGroups = searchedGroups.filter(g =>  g.shifts.every(s => s.status === 'confirmed'));
          const shiftOrder = shiftTimes.map(s => s.name);

          const emailedCount    = pendingGroups.filter(g => !!g.volunteer.lastConfirmEmailMsgId).length;
          const notContactedCount = pendingGroups.filter(g => !g.volunteer.lastConfirmEmailMsgId).length;

          const displayPendingGroups = pendingSubFilter === 'emailed'
            ? pendingGroups.filter(g => !!g.volunteer.lastConfirmEmailMsgId)
            : pendingSubFilter === 'not-contacted'
              ? pendingGroups.filter(g => !g.volunteer.lastConfirmEmailMsgId)
              : pendingGroups;

          const Section = ({ label, count, isOpen, onToggle, groups, subFilterBar }) => (
            <div style={{ marginBottom: '12px' }}>
              <button
                onClick={onToggle}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '12px 16px', border: '1px solid #e2e8f0', borderRadius: isOpen ? '8px 8px 0 0' : '8px',
                  background: '#f8fafc', cursor: 'pointer', fontWeight: 700, fontSize: '15px',
                  color: '#1e293b', textAlign: 'left',
                }}
              >
                <span>{label} <span style={{ fontWeight: 400, color: '#64748b', fontSize: '13px' }}>({count})</span></span>
                <span style={{ fontSize: '12px', color: '#94a3b8' }}>{isOpen ? '▼' : '▶'}</span>
              </button>
              {isOpen && (
                <div style={{ border: '1px solid #e2e8f0', borderTop: 'none', borderRadius: '0 0 8px 8px', padding: '8px' }}>
                  {subFilterBar && <div style={{ padding: '6px 4px 10px' }}>{subFilterBar}</div>}
                  {groups.length === 0
                    ? <div className="no-data" style={{ padding: '16px' }}>None.</div>
                    : groups.map(group => (
                        <VolunteerCard
                          key={group.volunteerId}
                          group={group}
                          shiftOrder={shiftOrder}
                          onStatusChange={handleStatusChange}
                          onMetaChange={handleMetaChange}
                          templateMap={templateMap}
                        />
                      ))
                  }
                </div>
              )}
            </div>
          );

          const notContactedGroups = pendingGroups.filter(g => !g.volunteer.lastConfirmEmailMsgId);
          const emailedGroups      = pendingGroups.filter(g => !!g.volunteer.lastConfirmEmailMsgId);

          const pendingSubFilterBar = (
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '12px', color: '#64748b', marginRight: '2px' }}>Show:</span>
              {[
                { key: 'all',           label: `All (${pendingGroups.length})` },
                { key: 'emailed',       label: `Email Sent (${emailedCount})` },
                { key: 'not-contacted', label: `Not Yet Contacted (${notContactedCount})` },
              ].map(f => (
                <button
                  key={f.key}
                  className={`filter-pill ${pendingSubFilter === f.key ? 'active' : ''}`}
                  style={{ fontSize: '12px', padding: '3px 10px' }}
                  onClick={() => setPendingSubFilter(f.key)}
                >
                  {f.label}
                </button>
              ))}
              <span style={{ borderLeft: '1px solid #e2e8f0', height: '20px', margin: '0 4px' }} />
              <button
                className="btn btn-primary"
                style={{ fontSize: '12px', padding: '3px 12px' }}
                disabled={!!bulkSending || notContactedGroups.length === 0}
                onClick={() => handleBulkSend(notContactedGroups, false)}
                title="Send initial confirmation email to all not-yet-contacted volunteers"
              >
                {bulkSending === 'initial'
                  ? `Sending… ${bulkProgress?.done ?? 0}/${bulkProgress?.total ?? 0}`
                  : `✉ Email All Not Contacted (${notContactedGroups.length})`}
              </button>
              <button
                className="btn btn-secondary"
                style={{ fontSize: '12px', padding: '3px 12px' }}
                disabled={!!bulkSending || emailedGroups.length === 0}
                onClick={() => handleBulkSend(emailedGroups, true)}
                title="Send reminder confirmation email to all volunteers already emailed but still pending"
              >
                {bulkSending === 'reminder'
                  ? `Sending… ${bulkProgress?.done ?? 0}/${bulkProgress?.total ?? 0}`
                  : `↻ Remind All Emailed (${emailedGroups.length})`}
              </button>
            </div>
          );

          return (
            <div className="confirmation-list">
              <Section
                label="Pending Confirmation"
                count={pendingGroups.length}
                isOpen={pendingOpen}
                onToggle={() => { setPendingOpen(o => !o); setPendingSubFilter('all'); }}
                groups={displayPendingGroups}
                subFilterBar={pendingSubFilterBar}
              />
              <Section
                label="Already Confirmed"
                count={confirmedGroups.length}
                isOpen={confirmedOpen}
                onToggle={() => setConfirmedOpen(o => !o)}
                groups={confirmedGroups}
              />
            </div>
          );
        })()}
      </div>
    </div>
  );
}

function VolunteerCard({ group, shiftOrder = [], onStatusChange, onMetaChange, templateMap = {} }) {
  const { volunteerId, volunteer, shifts } = group;
  const [notes, setNotes] = useState(group.notes || '');
  const [followUp, setFollowUp] = useState(group.followUp || false);
  const [collapsed, setCollapsed] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null); // null | 'sending' | 'sent' | 'error'
  const [emailError, setEmailError] = useState('');
  const [deliveryStatus, setDeliveryStatus] = useState(volunteer.lastConfirmEmailStatus || null);
  const [deliveryReason, setDeliveryReason] = useState(volunteer.lastConfirmEmailReason || null);
  const [checkingDelivery, setCheckingDelivery] = useState(false);
  const autoLang = isSpanish(volunteer.congregation) ? 'es' : 'en';
  const [lang, setLang] = useState(autoLang);

  const handleCheckStatus = useCallback(async () => {
    const msgId = volunteer.lastConfirmEmailMsgId;
    if (!msgId || checkingDelivery) return;
    setCheckingDelivery(true);
    try {
      const res = await fetch('/api/check-email-status', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messageId: msgId }),
      });
      const data = await res.json();
      if (res.ok) {
        setDeliveryStatus(data.status);
        setDeliveryReason(data.reason || null);
        await updateVolunteerEmailStatus(volunteerId, {
          messageId: msgId,
          status: data.status,
          reason: data.reason || null,
          sentAt: volunteer.lastConfirmEmailSentAt,
        });
      }
    } catch { /* silent — delivery check is best-effort */ }
    setCheckingDelivery(false);
  }, [volunteer.lastConfirmEmailMsgId, volunteer.lastConfirmEmailSentAt, volunteerId, checkingDelivery]);

  // Auto-check delivery status on mount if a pending message ID exists
  useEffect(() => {
    if (volunteer.lastConfirmEmailStatus === 'pending' && volunteer.lastConfirmEmailMsgId) {
      handleCheckStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSendEmail = async (e) => {
    e.stopPropagation();
    if (!volunteer.email) return alert('This volunteer has no email address on file.');
    if (emailStatus === 'sending') return;

    const confirmedShifts = shifts.filter(s => s.status === 'confirmed');
    const allShifts = confirmedShifts.length > 0 ? confirmedShifts : shifts;
    const volunteerName = `${volunteer.firstName} ${volunteer.lastName}`;
    const shiftsPayload = allShifts.map(s => ({ day: s.day, location: s.location, shiftTime: s.shiftTime }));

    setEmailStatus('sending');
    setEmailError('');

    try {
      const template = templateMap[`confirmation_${lang}`] || templateMap['confirmation_en'];

      // Generate a one-click confirm token (stores shift details for display on confirm page)
      const confirmToken = crypto.randomUUID();
      const shiftIds = allShifts.map(s => s.shiftId);
      const shiftDetails = allShifts.map(s => ({ day: s.day, location: s.location, shiftTime: s.shiftTime, shiftId: s.shiftId }));
      await saveConfirmationToken(volunteerId, confirmToken, shiftIds, shiftDetails);
      const confirmLink = `https://www.augustapwi.com/confirm?t=${confirmToken}`;

      const body = { to: volunteer.email, volunteerName, shifts: shiftsPayload };

      if (template?.emailHtmlPre) {
        const rendered = renderConfirmationEmail(template, volunteerName, shiftsPayload, confirmLink);
        body.html = rendered.html;
        body.subject = rendered.subject;
      }

      const res = await fetch('/api/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.success) {
        setEmailStatus('sent');
        setDeliveryStatus('pending');
        setDeliveryReason(null);
        const sentAt = new Date().toISOString();
        await updateVolunteerEmailStatus(volunteerId, {
          messageId: data.messageId || null,
          status: 'pending',
          reason: null,
          sentAt,
        });
        // Update the in-memory volunteer so auto-check works if card is not remounted
        volunteer.lastConfirmEmailMsgId = data.messageId || null;
        volunteer.lastConfirmEmailSentAt = sentAt;
        setTimeout(() => setEmailStatus(null), 4000);
      } else {
        setEmailStatus('error');
        setEmailError(data.error || 'Unknown error');
      }
    } catch (err) {
      setEmailStatus('error');
      setEmailError(err.message);
    }
  };

  useEffect(() => {
    setNotes(group.notes || '');
    setFollowUp(group.followUp || false);
  }, [group.notes, group.followUp]);

  const saveNotes = () => {
    onMetaChange(volunteerId, { confirmationNotes: notes, confirmationFollowUp: followUp });
  };

  const toggleFollowUp = (checked) => {
    setFollowUp(checked);
    onMetaChange(volunteerId, { confirmationNotes: notes, confirmationFollowUp: checked });
  };

  const allConfirmed = shifts.every(s => s.status === 'confirmed');
  const hasCannotMake = shifts.some(s => s.status === 'cannot-make');
  const cardClass = allConfirmed ? 'conf-card-confirmed' : hasCannotMake ? 'conf-card-cannot' : '';

  const sortedShifts = [...shifts].sort((a, b) => {
    const dayDiff = DAY_ORDER.indexOf(a.day) - DAY_ORDER.indexOf(b.day);
    if (dayDiff !== 0) return dayDiff;
    return shiftOrder.indexOf(a.shiftTime) - shiftOrder.indexOf(b.shiftTime);
  });

  return (
    <div className={`volunteer-conf-card ${cardClass} ${followUp ? 'conf-card-followup' : ''}`}>
      {/* Header — click to collapse/expand */}
      <div className="conf-card-header conf-card-header-clickable" onClick={() => setCollapsed(!collapsed)}>
        <div className="conf-card-name-row">
          <span className="conf-collapse-icon">{collapsed ? '▶' : '▼'}</span>
          <span className="conf-volunteer-name">
            {volunteer.lastName}, {volunteer.firstName}
          </span>
          {volunteer.volunteerType === 'smpw' && <span className="badge badge-smpw">SMPW</span>}
          {volunteer.isKeyman && <span className="badge badge-keyman">Keyman</span>}
          {followUp && <span className="badge badge-followup">Follow-Up</span>}
          {allConfirmed && <span className="badge badge-ok">All Confirmed</span>}
          {hasCannotMake && <span className="badge badge-warn">Has Cannot Make</span>}
        </div>
        <div className="conf-card-contact-row" onClick={e => e.stopPropagation()}>
          <span className="conf-congregation">{volunteer.congregation || 'No congregation'}</span>
          {volunteer.phone && (
            <a href={`tel:${volunteer.phone}`} className="btn-call-small">
              Call {volunteer.phone}
            </a>
          )}
          {volunteer.email && <span className="conf-email">{volunteer.email}</span>}
          {volunteer.email && (
            <>
              <button
                className={`msg-lang-badge ${lang}`}
                style={{ fontSize: '11px', cursor: 'pointer', border: lang !== autoLang ? '2px solid #f59e0b' : undefined }}
                onClick={e => { e.stopPropagation(); setLang(l => l === 'es' ? 'en' : 'es'); }}
                title="Toggle email language (EN/ES)"
              >
                {lang.toUpperCase()}
              </button>
              <button
                className={`btn-send-email${emailStatus === 'sent' ? ' sent' : emailStatus === 'error' ? ' error' : ''}`}
                onClick={handleSendEmail}
                disabled={emailStatus === 'sending'}
                title={emailStatus === 'error' ? emailError : `Send confirmation email (${lang.toUpperCase()})`}
              >
                {emailStatus === 'sending' ? 'Sending…' : emailStatus === 'sent' ? '✓ Sent' : emailStatus === 'error' ? '✗ Failed' : '✉ Email'}
              </button>
            </>
          )}
          {deliveryStatus && (
            <span
              className={`email-delivery-badge email-delivery-${deliveryStatus}`}
              title={deliveryReason || undefined}
            >
              {deliveryStatus === 'delivered' && '✓ Delivered'}
              {deliveryStatus === 'blocked'   && '✗ Blocked'}
              {deliveryStatus === 'bounced'   && '✗ Bounced'}
              {deliveryStatus === 'deferred'  && '⏳ Deferred'}
              {deliveryStatus === 'pending'   && (checkingDelivery ? 'Checking…' : '⏳ Pending')}
            </span>
          )}
          {deliveryStatus && volunteer.lastConfirmEmailMsgId && (
            <button
              className="btn-refresh-delivery"
              onClick={e => { e.stopPropagation(); handleCheckStatus(); }}
              disabled={checkingDelivery}
              title="Re-check delivery status"
            >
              ↻
            </button>
          )}
        </div>
      </div>

      {!collapsed && (
        <>
          {/* Shifts */}
          <div className="conf-card-shifts">
            <div className="conf-shifts-heading">
              Shifts assigned ({shifts.length})
            </div>
            {sortedShifts.map(shift => (
              <div key={shift.shiftId} className={`conf-shift-row conf-shift-${shift.status}`}>
                <div className="conf-shift-info">
                  <span className="conf-day">{shift.day}</span>
                  <span className="conf-location">📍 {shift.location}</span>
                  <span className="conf-time">⏰ {shift.shiftTime}</span>
                </div>
                <select
                  value={shift.status}
                  onChange={(e) => onStatusChange(volunteerId, shift.shiftId, e.target.value)}
                  className={`status-select conf-status-${shift.status}`}
                >
                  <option value="pending">⏳ Pending</option>
                  <option value="confirmed">✅ Confirmed</option>
                  <option value="left-vm">📞 Left VM</option>
                  <option value="cannot-make">❌ Cannot Make</option>
                  <option value="no-response">🔇 No Response</option>
                </select>
              </div>
            ))}
          </div>

          {/* Follow-up + Notes */}
          <div className="conf-card-meta">
            <label className="followup-checkbox-label" onClick={e => e.stopPropagation()}>
              <input
                type="checkbox"
                checked={followUp}
                onChange={(e) => toggleFollowUp(e.target.checked)}
              />
              <span>Needs Follow-Up</span>
            </label>
            <div className="conf-notes-row">
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                onBlur={saveNotes}
                onClick={e => e.stopPropagation()}
                placeholder="Notes (saved on blur)..."
                className="conf-notes-textarea"
                rows={2}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default ConfirmationTracking;
